import {createContext, PropsWithChildren, useEffect, useRef, useState, useCallback} from "react";
import {pb} from "@/lib/pocketbase";
import {AuthRecord, ClientResponseError, RecordAuthResponse, RecordModel} from "pocketbase";
import {AsyncStorageAuthStore} from "@/auth/AsyncStorageAuthStore";
import {useQueryClient} from "@tanstack/react-query";
import {appDataKeys} from "@/data/app-data-keys";
import {getImpersonateToken} from "@/data/app-data-api";
import {appConfig} from "@/app-config";
import * as Sentry from "@sentry/react-native";
import {resetChatClient} from "@/lib/chat-client";

interface AuthContextType {
    user: AuthRecord | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    requestOTP: (email: string) => Promise<string>;
    verifyOTP: (otpId: string, password: string) => Promise<void>;
    loginWithToken: (token: string) => Promise<void>;
    interalLogout: () => Promise<void>;
}

const defaultAuthContext: AuthContextType = {
    user: null,
    isLoading: false,
    isAuthenticated: false,
    requestOTP: (email: string) => Promise.reject('not implemented'),
    verifyOTP: (otpId: string, password: string) => Promise.reject('not implemented'),
    loginWithToken: (token: string) => Promise.reject('not implemented'),
    interalLogout: () => Promise.resolve(),
};

export const AuthContext = createContext(defaultAuthContext);

export const AuthProvider = ({children}: PropsWithChildren) => {
    const [user, setUser] = useState(pb.authStore.record);
    // who the live chat client was minted for, so a plain token refresh is not mistaken for a
    // change of user
    const chatUserRef = useRef<string | null>(pb.authStore.record?.id ?? null);
    const [isLoading, setIsLoading] = useState(true);
    const queryClient = useQueryClient();

    useEffect(() => {
        const initAuth = async () => {
            try {
                // Wait for AsyncStorage restoration to complete
                await (pb.authStore as AsyncStorageAuthStore).isRestored;

                // Now we can safely check auth state
                if (pb.authStore.isValid) {
                    try {
                        await pb.collection('users').authRefresh();
                    } catch (err) {
                        if (err instanceof ClientResponseError) {
                            if (err.status === 401 || err.status === 403) {
                                console.log('Auth token invalid, clearing auth');
                                pb.authStore.clear();
                            }
                        }
                    }
                } else if (pb.authStore.token) {
                    // Token exists but is expired — clear stale auth so Authenticated
                    // guard redirects to login before any API calls go out.
                    console.log('Auth token expired, clearing auth');
                    pb.authStore.clear();
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
            } finally {
                setIsLoading(false);
            }
        };

        // Listen for auth changes
        const unsubscribe = pb.authStore.onChange((token, record) => {
            setUser(record);
            Sentry.setUser(record ? {id: record.id} : null);

            // The chat token is minted for one user, so its client outlives its owner: without
            // this a logout leaves a socket connected as the previous account and the next user
            // inherits it. Identity, not token — authRefresh fires this listener too, and that is
            // the same user, so resetting there would drop a perfectly healthy socket.
            const nextUserId = record?.id ?? null;
            if (chatUserRef.current !== nextUserId) {
                chatUserRef.current = nextUserId;
                resetChatClient();
            }

            if (record) {
                // User logged in or token refreshed — refetch with new token
                void queryClient.invalidateQueries({queryKey: appDataKeys.detail()});
            } else {
                // Auth cleared — cancel in-flight queries to stop retry storm
                void queryClient.cancelQueries({queryKey: appDataKeys.detail()});
            }
        });

        initAuth();

        return unsubscribe;
    }, [queryClient]);

    const requestOTP = useCallback(async (email: string) => {
        try {
            if (appConfig.allowedImpersonations.includes(email)) {
                return 'IMPERSONATE_' + email;
            } else {
                const res = await pb.collection('users').requestOTP(email.toLowerCase());
                return res.otpId;
            }
        } catch (error: any) {
            console.error('OTP request error:', error);
            throw Error(error.message);
        }
    }, []);

    const loginWithToken = useCallback(async (token: string) => {
        try {
            // Save the token to PocketBase auth store
            pb.authStore.save(token, null);

            // Refresh auth to get the user record
            const authData = await pb.collection('users').authRefresh();
            setUser(authData.record);

            // Invalidate appData to force refetch with new user's token
            await queryClient.invalidateQueries({queryKey: appDataKeys.detail()});
        } catch (error: any) {
            console.error('Token login error:', error);
            // Clear invalid token
            pb.authStore.clear();
            throw Error(error.message);
        }
    }, [queryClient]);

    const verifyOTP = useCallback(async (otpId: string, password: string) => {
        try {
            let authData: RecordAuthResponse<RecordModel>;
            if (otpId.startsWith('IMPERSONATE_') && password === '12345678') {
                const email = otpId.replace('IMPERSONATE_', '');
                const token = await getImpersonateToken(email.toLowerCase());
                if (token) {
                    loginWithToken(token);
                }
                // call imperson
            } else {
                authData = await pb.collection('users').authWithOTP(otpId, password);
                setUser(authData.record);
            }
        } catch (error: any) {
            console.error('OTP verification error:', error);
            throw Error(error.message);
        }
    }, [loginWithToken]);

    const interalLogout = useCallback(async () => {
        pb.authStore.clear();
        setUser(null);
        Sentry.setUser(null);
        // Clear all query cache (in memory and persisted)
        queryClient.clear();
    }, [queryClient]);

    const value = {
        user,
        isLoading,
        isAuthenticated: !!user,
        requestOTP,
        verifyOTP,
        loginWithToken,
        interalLogout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
